import {
  secureBusinessSectionsVulcan,
  existingEquipmentVulcan
} from '../../utils/constant';

// The equipment-blob section keys `getFormData` (formStepFMVulcanUtils.js)
// reads via `getEquipmentGroupFormData(fields[key])` — confirmed to be
// exactly `secureBusinessSectionsVulcan` (constant.js) plus
// `existingEquipmentVulcan`, which that constant omits. Equipment sections
// are `custom`/blob-shaped (vulcanContentTypeMap.js) and never tree-walked
// into `fieldStates`; their data lives only in `engine.values[key]`.
const EQUIPMENT_BLOB_KEYS = [
  ...secureBusinessSectionsVulcan,
  existingEquipmentVulcan
];

/**
 * Build the SAVE payload's field bag directly from the whole-order engine —
 * SOF-3402/SOF-3439 Stage 4c-iv-a. Replaces `mergeEngineValuesIntoLegacyBag`,
 * removed in Stage 4c-iv-b together with the `formFields` state it read
 * (see git history for that function if it's ever needed for reference). It
 * started from the per-step legacy `formFields` bag and
 * overlaid the engine on top. Two things made that necessary that no longer
 * apply:
 *
 *  - `engine.fieldStates` used to be single-step, so a field from a step the
 *    user had already passed through had no fieldState this render, and the
 *    legacy bag (plus an `orderData` refresh sweep — finding #21) was the
 *    only whole-order source. Now `VulcanFormStepRunner` builds a multi-step
 *    `definition`, so `fieldStates` spans the WHOLE order on its own
 *    (`multiStepDefinition.test.js` asserts this directly — "the finding #21
 *    bug is now impossible at the source"). No refresh sweep needed.
 *  - The legacy bag's shape (`{...field, name, type, value, isVisible,
 *    options, monthlyPayment, oneTimePrice, …}`) looked richer than
 *    `fieldStates`. It isn't: `buildFieldStates` (the package) spreads the
 *    whole field definition — `{...field, name, type, value, isVisible,
 *    options, disabled, error, touched, dirty}` — before merging fieldRules
 *    patches (which is how `monthlyPayment`/`oneTimePrice`/etc. land on
 *    entries already, via `rules/secureBusiness.js` and friends). So
 *    `fieldStates` entries are a SUPERSET of what the legacy bag's leaf
 *    entries held, confirmed against the package source directly.
 *
 * What genuinely doesn't come from `fieldStates`:
 *  - `.checked` for Checkbox-type fields. The package tracks only `.value`
 *    (a boolean) for these — its own default `FieldCheckbox` component
 *    derives `checked: Boolean(value)` purely for rendering. `getFormData`
 *    branches `type === 'Checkbox' ? checked : value`, so this bag derives
 *    `.checked` the same way, for the same reason.
 *  - Equipment blobs (see `EQUIPMENT_BLOB_KEYS` above) — `custom`-kind items
 *    are never tree-walked into `fieldStates`; their data lives only in
 *    `engine.values[key]`, seeded/written by `EquipmentSectionForm` via the
 *    `useFormFieldsBridge` bridge in `vulcanSectionRegistry.js`.
 *  - Writes the engine's field tree never walks at all — e.g.
 *    `userDetails`'s `sameAsCheckBox` sibling entry, which isn't a
 *    `formStepFields` child so `collectAllFields` never sees it, but
 *    `form.setValue` still records it in `engine.values`. Confirmed
 *    real-data bug (SOF-3402/SOF-3439 session notes): this checkbox's own
 *    value was silently dropped from every submit. Swept in as a plain leaf
 *    field below, same as the equivalent sweep in the legacy-bag version.
 *
 * Ancillary blobs (redeemOfferData, tsbData, businessOptikTvTableData, …)
 * are deliberately NOT included here — `getFormData`'s callers read those
 * directly off `engine.values` as explicit arguments (see
 * `buildEngineFormData` in formStepVulcan.js), the same six-blob convention
 * `pricingAdapter.js` documents. Wrapping them here as fake leaf fields would
 * resurrect the exact null/wrong-shape crash the legacy-bag version's own
 * scalar-only rule existed to avoid.
 *
 * @param {{fieldStates?: Object, values?: Object}} engine
 * @returns {Object}
 */
export const buildEngineFieldBag = engine => {
  const fieldStates = engine.fieldStates || {};
  const values = engine.values || {};
  const merged = {};

  Object.keys(fieldStates).forEach(k => {
    const state = fieldStates[k];
    if (state.type !== 'Checkbox') {
      // The engine clears a newly hidden multi-value field to `[]`; legacy saved
      // hidden fields as ''.
      const isHiddenEmptyArray =
        state.isVisible === false &&
        Array.isArray(state.value) &&
        state.value.length === 0;
      merged[k] = isHiddenEmptyArray ? { ...state, value: '' } : state;
      return;
    }
    // The engine defaults every Checkbox to `false`; a hidden one must save
    // empty like other hidden fields, not as an explicit "No".
    merged[k] = {
      ...state,
      checked: state.isVisible === false ? '' : Boolean(state.value)
    };
  });

  EQUIPMENT_BLOB_KEYS.forEach(key => {
    if (values[key] !== undefined) merged[key] = values[key];
  });

  Object.keys(values).forEach(k => {
    if (k in merged) return;
    const v = values[k];
    if (v === null || typeof v === 'object') return;
    merged[k] = { value: v, isVisible: true };
  });

  return merged;
};
